import i18n from 'i18n'
import parse from 'html-react-parser'

import { extractNBody } from 'utils/mailBodyHelpers'

import attachmentHelper, { Attachment } from 'pages/main/components/Attachment/helper'
import { StorageType, SharingFilesFactory } from 'services/sharing-file/SharingFilesHtmlTemplate'

import { getContactInfo } from 'pages/main/data/avatarService'
import dataManager from 'pages/main/data/DataManager'

import settings from 'popups/Settings'

import { getThreadViewInfo } from 'pages/main/components/ThreadView/viewerExt'
import emailHelper from 'pages/main/components/ThreadView/emailHelper'
import attachmentJSONHelper from 'pages/main/components/ThreadView/attachmentHelper'
import { sendScheduledEmail } from 'pages/main/data/ScheduleMailbox'
import secureSendApiHelper, { type SendSecureMailData, AttachmentItem } from 'services/secure-send'
import readReceiptApiHelper, { type SendRequest } from 'services/read-receipt'
import { generateEMLUsingEmailObj, validateEMLContent } from 'pages/main/data/JMAPMailbox'

import { sanitizeEmail, stripInvisible } from 'libs/emailSanitize'

import { buildBase64Attachments } from './buildBase64Attachments'
import { generateNineMailId } from './nineMailId'
import { computeThreadingFields, type ThreadingFields } from './threading'

import dayjs from 'dayjs'

import { timeFormat } from 'utils'
import { showResult, createPromiseMgr, formatBytes, ErrorEx } from 'utils/ui'

export { generateNineMailId } from './nineMailId'

// Type definitions
type ScheduleSendMode = 'normal' | 'read_receipt' | 'secure' | 'secure_no_read_receipt'

interface SecureOptions {
  expires_at: string
}

interface SendScheduledOptions {
  secureSendEnabled?: boolean
  secureExpiresAt?: string | null
  readReceiptEnabled?: boolean
}

interface EmailInfo {
  email: string
  name?: string
  [key: string]: any
}

interface ContactInfo {
  id: string
  name?: string | null
  email: string
  avatarUrl?: string | null
  [key: string]: any
}

interface EmailData {
  to?: (EmailInfo | string)[]
  cc?: (EmailInfo | string)[]
  bcc?: (EmailInfo | string)[]
  from?: (EmailInfo | string)[]
  subject?: string
  htmlBody?: string
  htmlText?: string
  bodyIsHTML?: boolean
  sharingAttachments?: Attachment[]
  attachments?: Attachment[]
  inReplyTo?: string[]
  references?: string[]
  threadId?: string
  replyTo?: EmailInfo[]
  [key: string]: any
}

interface EmailObj {
  data: {
    id: string
    messageId: string[]
    references?: string[]
    threadId?: string
    mailboxIds: { [key: string]: boolean }
    [key: string]: any
  }
  reqDetail?: () => Promise<any>
  [key: string]: any
}

interface FormAPIs {
  getEmailValues: (forSave?: boolean) => EmailData
  getNineBodyId: () => string
  isEmpty: () => boolean
  isModified: () => boolean
}

interface SendHelperOptions {
  cmd?: string | null
  draft?: EmailObj | null
  prevEmail?: EmailObj | null
  extValues?: any
  onSended?: (result: { mailData: EmailData; prevDraft: EmailObj | null }) => void
  onSaved?: (result: {
    mailData: EmailData
    prevDraft: EmailObj | null
    draft: EmailObj
    isChanged: boolean
  }) => void
  onError?: (error: Error | string) => void
  onTitleChanged?: (title: string) => void
  userInfo?: any
  replyTo?: string | null
  /**
   * 본 작성 세션의 nine-mail-id. 미지정 시 constructor 가 자동으로 `ncompose-{uuid}` 발급.
   * 테스트 fixture 에서 결정성을 위해 직접 주입 가능. JMAP emailId 와는 별개 도메인이므로
   * `EmailObj.data.id` 를 여기에 넘기면 안 된다.
   */
  nineMailId?: string
}

interface EmailListResult {
  dataList: Array<{
    data: EmailInfo
  }>
}

interface CreateEmailResult {
  createdEmail?: {
    created?: { [key: string]: { id: string } }
    notCreated?: {
      [key: string]: {
        type: string
        properties?: string[]
        invalidRecipients?: string[]
      }
    }
  }
  emailSubmissionSet?: {
    created?: { [key: string]: { id: string } }
    notCreated?: {
      [key: string]: {
        type: string
        properties?: string[]
        invalidRecipients?: string[]
      }
    }
  }
  test?: boolean
}

interface PromiseMgr {
  add: (resolve: (value: any) => void, reject: (reason?: any) => void) => void
  resolveAll: (value: any) => void
  rejectAll: (reason?: any) => void
}

interface ResultUI {
  close: () => void
}

interface ValidationResult {
  isValid: boolean
  errors: string[]
}

interface DataManager {
  getTempEmailList: () => Promise<EmailListResult>
  createEmail: (mailData: EmailData, detail: any, isSend: boolean) => Promise<CreateEmailResult>
  getEmailById: (id: string) => EmailObj | null
  applyEmailById: (id: string, force: boolean) => Promise<EmailObj>
  addTempEmail: (emailInfo: EmailInfo) => void
  addRecentSendEmails: (contactList: (ContactInfo | null)[]) => void
  getMailboxById: (id: string) => any
  getMailboxForRole: (role: string) => any
  // 발송이 진행 중인 draft id 집합. viewer [초안] 배지 결정(shouldShowDraftBadge)에서 사용.
  sendingEmailIds: Set<string>
  outbox: {
    enabled: boolean
    add: (
      emailData: EmailData,
      attachments?: Attachment[],
      draftId?: string | EmailObj,
    ) => Promise<any>
  }
}

class SendHelper {
  cmd: string | null
  options: SendHelperOptions
  curDraft: EmailObj | null
  isSended: boolean
  isDeleteCalled: boolean
  isSendCalled: boolean
  dataMgr: DataManager
  attachmentHelper: typeof attachmentHelper
  formAPIs: FormAPIs | null
  userInfo: any
  replyTo: string | null
  isSending: boolean
  isSaving: boolean
  sendPromiseMgr: PromiseMgr
  savePromiseMgr: PromiseMgr
  attachmentsLimit: number
  /**
   * 본 작성 세션의 nine-mail-id (NgwEditor 0.2.11 의 4-region 외곽 wrapper + region id prefix).
   * JMAP emailId 와는 별개 도메인 — 두 ID 의 명명 혼선을 막기 위해 `nineMailId` 로 통일.
   */
  readonly nineMailId: string

  constructor(options?: SendHelperOptions) {
    const self = this

    self.cmd = options != null ? options.cmd || null : null

    self.options = options || {}

    self.curDraft = options != null ? options.draft || null : null

    self.isSended = false

    self.isDeleteCalled = false

    self.isSendCalled = false

    // sendHelper가 생성된 프로세스의 dataManager를 저장.
    self.dataMgr = dataManager as DataManager

    self.attachmentHelper = attachmentHelper

    self.formAPIs = null

    self.userInfo = options != null ? options.userInfo || null : null

    self.replyTo = options != null ? options.replyTo || null : null

    self.isSending = false

    self.isSaving = false

    self.sendPromiseMgr = createPromiseMgr()

    self.savePromiseMgr = createPromiseMgr()

    self.attachmentsLimit = 25 * 1024 * 1024 // 25MB

    this.nineMailId = options?.nineMailId ?? generateNineMailId()
  }

  getNineMailId(): string {
    return this.nineMailId
  }

  private getEmailInfoList = async (): Promise<EmailListResult> => {
    return await this.dataMgr.getTempEmailList()
  }

  private getMailData = async (obj: EmailData): Promise<EmailData> => {
    const retObj: EmailData = {
      ...obj,
    }

    let dataList: EmailListResult['dataList'] = []
    try {
      // 받은 메일 중에서 이메일 정보를 수집한 내용으로 검색하는 부분이기 때문에
      // 문제 발생시 이 부분은 건너뛰고 메일을 발송해도 문제가 되지 않는다.
      // 다른 에러들은 팝업 메시지를 표시하고, 메일 발송 중지.
      const result = await this.getEmailInfoList()
      dataList = result.dataList
    } catch (err) {
      console.error(' - getEmailInfoList error - ', err)
    }

    const findEmailInfo = (email: string): EmailInfo => {
      const item = dataList.find((item) => {
        return item.data.email === email
      })

      if (item == null) {
        return { email, name: email }
      }

      return item.data
    }

    // OMD-204: 모든 발송 경로가 통과하는 지점이므로 여기서 수신자 email/name의 비가시 문자를 정제한다.
    // 추가 키(avatarUrl/id 등)는 스프레드로 보존하고, name은 원래 값이 있을 때만 정제(undefined 유지).
    const sanitizeInfo = (info: EmailInfo): EmailInfo => {
      const cleaned: EmailInfo = { ...info, email: sanitizeEmail(info.email) }
      if (info.name != null) {
        cleaned.name = stripInvisible(info.name)
      }
      return cleaned
    }

    const normalizeRecipient = (item: EmailInfo | string): EmailInfo =>
      sanitizeInfo(typeof item === 'string' ? findEmailInfo(item) : item)

    retObj.from = (obj.from || []).map(normalizeRecipient)
    retObj.to = (obj.to || []).map(normalizeRecipient)
    retObj.cc = (obj.cc || []).map(normalizeRecipient)
    retObj.bcc = (obj.bcc || []).map(normalizeRecipient)

    retObj.sharingAttachments = (obj.sharingAttachments || []).filter((attachment) => {
      return attachment.sendType === 'sharingFile' && attachment.downloadUrl != null
    })

    retObj.attachments = (obj.attachments || []).filter((attachment) => {
      return attachment.sendType === 'jmap'
    })

    retObj.bodyIsHTML = true

    if (retObj.sharingAttachments.length > 0 && obj.htmlBody != null) {
      const resourceProvider = {
        getString: (key: string): string => {
          switch (key) {
            case 'unlimited':
              return i18n.t('email.attachment.unlimited')
            case 'no_limit':
              return i18n.t('email.attachment.noLimit')
            case 'visit_limit':
              return i18n.t('email.attachment.visitLimit')
            case 'expire_date':
              return i18n.t('email.attachment.expireDate')
          }
          return key
        },
        getQuantityText: (key: string, quantity: number): string => {
          switch (key) {
            case 'sharing_files_quantity':
              if (quantity === 1) {
                return i18n.t('email.attachment.sharingFileQuantity')
              }
              return i18n.t('email.attachment.sharingFilesQuantity', { quantity })
          }
          return key + ': ' + quantity.toString()
        },
        formatSize: (size: number): string => {
          return formatBytes(size, 1, false, 2) as string
        },
      }

      const sharingFiles = retObj.sharingAttachments.map((attachment) => {
        const { file, downloadUrl } = attachment
        return {
          storageType: StorageType.OfficeMailStorage,
          name: file.name,
          url: downloadUrl!,
          size: file.size,
        }
      })

      const sharingOptions = {
        getViewingExpireDate: (): string => {
          const { dateFormat } = settings.getTimeFormat()
          const expiresAt = timeFormat(retObj.sharingAttachments![0].expiresAt!, dateFormat)
          return expiresAt
        },
        visitLimit: retObj.sharingAttachments[0].downloadLimit!,
      }
      const sharingFilesHtmlTemplate = SharingFilesFactory.createSharingAttachment(
        resourceProvider,
        sharingFiles,
        sharingOptions,
      )
      const sharingFilesHtml = sharingFilesHtmlTemplate.getHtmlTemplate()
      console.log(' - sharingFilesHtml - ', sharingFilesHtml)

      const nineBodyId = this.formAPIs!.getNineBodyId()

      const domParser = new DOMParser()
      const doc = domParser.parseFromString(obj.htmlBody, 'text/html')
      const nBody = extractNBody(doc, nineBodyId)
      const divElem = doc.createElement('div')
      divElem.contentEditable = 'false'
      // 2026-05-19 — sharingFilesHtml 을 DOMParser 로 파싱 후 자식 노드 이전 (innerHTML 회피).
      // sharingFilesHtml 은 SharingFilesFactory.createSharingAttachment 의 template literal 결과
      // 로 첨부 파일 메타데이터 (이름/URL/크기) 가 포함되며, 그 값들이 attachment 객체에서 온다.
      const sharingDoc = domParser.parseFromString(sharingFilesHtml, 'text/html')
      // appendChild + importNode 가 동일 doc 으로 노드를 이동시키므로 별도 removeChild 불필요.
      // Array.from 으로 스냅샷 후 순회 — appendChild 의 live HTMLCollection 변형 회피.
      Array.from(sharingDoc.body.childNodes).forEach((node) => {
        divElem.appendChild(doc.importNode(node, true))
      })
      nBody.insertBefore(divElem, nBody.firstChild)

      retObj.htmlBody = doc.documentElement.outerHTML
    } else {
      retObj.htmlBody = obj.htmlBody
    }

    retObj.htmlBodyText = obj.htmlText

    if (retObj.attachments.length > 0) {
      const promiseList = retObj.attachments.map((attachment) => {
        return attachment.checkUpload((evt, progress) => {
          console.log(' - upload progressCB - ', evt, progress)
        })
      })

      await Promise.all(promiseList).catch((err) => {
        this.saveToOutbox(obj, this.curDraft).catch((e) => {
          console.error('[SendHelper] saveToOutbox in upload error handler failed:', e)
        })
        throw err
      })
      console.log(' - upload success - ')
    }

    // account.replyTo → Reply-To 헤더. 모든 발송 경로가 통과하는 공통 지점에서 한 번만 주입한다.
    // save(임시저장)도 이 지점을 거치므로 draft 자체에 replyTo가 실려, 저장된 draft를 그대로
    // 발송하는 경로(jamUtil.createEmail의 previousDraft submit)에서도 Reply-To가 유지된다.
    // this.replyTo(생성자 주입)를 우선하되, 옵션 미전달 caller 방어를 위해 설정값을 fallback으로 조회한다.
    const replyToAddress = this.replyTo ?? settings.getValue('account')?.replyTo
    if (
      replyToAddress != null &&
      typeof replyToAddress === 'string' &&
      replyToAddress.trim().length > 0
    ) {
      retObj.replyTo = [{ email: replyToAddress.trim(), name: '' }]
    }

    return retObj
  }

  private saveToOutbox = (obj: EmailData, draft: EmailObj | null): Promise<boolean> => {
    if (this.dataMgr.outbox.enabled !== true) {
      return Promise.resolve(true)
    }

    return this.getEmailInfoList().then(({ dataList }) => {
      const retObj: EmailData = {
        ...obj,
      }

      const findEmailInfo = (email: string): EmailInfo => {
        const item = dataList.find((item) => {
          return item.data.email === email
        })

        if (item == null) {
          return { email, name: email }
        }

        return item.data
      }

      retObj.from = (obj.from || []).map((item) => {
        if (typeof item === 'string') {
          return findEmailInfo(item)
        }
        return item
      })
      retObj.to = (obj.to || []).map((item) => {
        if (typeof item === 'string') {
          return findEmailInfo(item)
        }
        return item
      })
      retObj.cc = (obj.cc || []).map((item) => {
        if (typeof item === 'string') {
          return findEmailInfo(item)
        }
        return item
      })
      retObj.bcc = (obj.bcc || []).map((item) => {
        if (typeof item === 'string') {
          return findEmailInfo(item)
        }
        return item
      })
      retObj.bodyIsHTML = true

      retObj.htmlBody = obj.htmlBody

      retObj.htmlBodyText = obj.htmlText

      return this.dataMgr.outbox
        .add(retObj, obj.attachments, draft || undefined)
        .then(() => {
          console.log(' - saveToOutbox success - ')
          return true as boolean
        })
        .catch((err) => {
          console.log(' - saveToOutbox error - ', err)
          throw err
        })
    })
  }

  private createEmail = async (
    mailData: EmailData,
    draft: EmailObj | null,
    isSend: boolean,
  ): Promise<CreateEmailResult> => {
    console.log(' - createEmail : ', mailData, draft, isSend)

    const prevEmail = this.getPrevEmail()
    if (prevEmail != null) {
      // 스레딩 헤더 주입. 회신(reply/replyAll)은 In-Reply-To + References 를,
      // 전달(forward)은 References 만 싣는다(getThreadingFields() 가 in_reply_to 를 비운다).
      const threading = this.getThreadingFields()
      if (threading != null) {
        const { in_reply_to, references } = threading
        if (
          in_reply_to != null &&
          // 이미 추가된 정보인지 확인.
          (mailData.inReplyTo == null ||
            mailData.inReplyTo.length === 0 ||
            mailData.inReplyTo[0] !== in_reply_to)
        ) {
          mailData.inReplyTo = [in_reply_to]
        }
        mailData.references = references
      }

      // threadId 는 발신자 본인 화면 묶음용이므로 forward 포함 기존 동작을 유지한다.
      const { threadId } = prevEmail.data
      if (threadId != null) {
        mailData.threadId = threadId
      }
    }

    const detail = draft != null && draft.reqDetail != null ? await draft.reqDetail() : null
    const ret = await this.dataMgr.createEmail(mailData, detail, isSend)
    const { createdEmail, emailSubmissionSet } = ret

    if (createdEmail != null && createdEmail.notCreated != null) {
      const key0 = Object.keys(createdEmail.notCreated)[0]
      const val = createdEmail.notCreated[key0]
      if (val != null) {
        let error: Error | null = null
        if (val.type === 'invalidProperties') {
          error = new Error(`invalidProperties - [ ${val.properties!.join(', ')} ]`)
        } else if (val.type === 'invalidRecipients') {
          const { invalidRecipients } = val
          const type = 'invalidRecipients'
          const data = invalidRecipients
          const message = `invalidRecipients - ${invalidRecipients!.join(', ')}`
          const msgNode = parse(
            `${i18n.t('email.error.invalidRecipients')}<br /><br />  ${invalidRecipients!.join('<br />  ')})`,
          )
          error = new ErrorEx<string[]>(message, type, data, msgNode)
        } else {
          error = new Error(`email create error - ${val.type}`)
        }
        throw error
      }
    }

    if (emailSubmissionSet != null && emailSubmissionSet.notCreated != null) {
      const key0 = Object.keys(emailSubmissionSet.notCreated)[0]
      const val = emailSubmissionSet.notCreated[key0]
      if (val != null) {
        let error: Error | null = null
        if (val.type === 'invalidProperties') {
          error = new Error(`invalidProperties - [ ${val.properties!.join(', ')} ]`)
        } else if (val.type === 'invalidRecipients') {
          const { invalidRecipients } = val
          const type = 'invalidRecipients'
          const data = invalidRecipients
          const message = `invalidRecipients - ${invalidRecipients!.join(', ')}`
          const msgNode = parse(
            `${i18n.t('email.error.invalidRecipients')}<br /><br />  ${invalidRecipients!.join('<br />  ')}`,
          )
          error = new ErrorEx<string[]>(message, type, data, msgNode)
        } else {
          error = new Error(`email send error - ${val.type}`)
        }
        throw error
      }
    }

    return ret
  }

  saveLocal = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      const { curDraft } = this

      if (this.formAPIs != null) {
        const formData = this.formAPIs.getEmailValues()

        this.saveToOutbox(formData, curDraft)
          .then((ret) => {
            resolve(ret)
          })
          .catch((err) => {
            reject(err)
          })
      }
    })
  }

  send = async (): Promise<any> => {
    this.isSendCalled = true

    if (this.isSaving === true) {
      // [fix] save가 hang되면 send도 무한 대기하는 문제 방지. 30초 후 타임아웃.
      const SAVE_WAIT_TIMEOUT_MS = 30_000
      let saveTimeoutId: ReturnType<typeof setTimeout>
      const saveTimeoutPromise = new Promise<any>((_, reject) => {
        saveTimeoutId = setTimeout(
          () => reject(new Error(i18n.t('email.result.saveTimeout'))),
          SAVE_WAIT_TIMEOUT_MS,
        )
      })
      try {
        await Promise.race([this.save(), saveTimeoutPromise])
      } catch (err) {
        console.error(' - send: save wait failed - ', err)
        this.isSaving = false
      } finally {
        clearTimeout(saveTimeoutId!)
      }
      return this.send()
    }

    return new Promise((resolve, reject) => {
      // 발송 중 draft id. 진입 시 sendingEmailIds에 등록하고 실패·타임아웃 시 해제한다
      // (타임아웃 = 발송 포기이므로 [초안] 표시를 복원하는 것이 맞다).
      const sendingDraftId =
        this.curDraft != null && this.curDraft.data != null ? this.curDraft.data.id : null

      // [fix] JMAP 요청이 hang되면 sendPromiseMgr가 settle되지 않는 문제 방지. 120초 후 강제 reject.
      const SEND_OPERATION_TIMEOUT_MS = 120_000
      const sendTimeoutId = setTimeout(() => {
        this.isSending = false
        if (sendingDraftId != null) {
          this.dataMgr.sendingEmailIds.delete(sendingDraftId)
        }
        this.sendPromiseMgr.rejectAll(new Error(i18n.t('email.result.sendTimeout')))
      }, SEND_OPERATION_TIMEOUT_MS)

      this.sendPromiseMgr.add(
        (data) => {
          clearTimeout(sendTimeoutId)
          resolve(data)
        },
        (err) => {
          clearTimeout(sendTimeoutId)
          reject(err)
        },
      )

      if (this.isSending !== true) {
        this.isSending = true
        const { onSended, onError } = this.options
        const { curDraft } = this

        // 발송 시작 시 draft id를 "발송 중" 집합에 등록(viewer [초안] 차단). generateData가 이 id는
        // draft=false로 강제 → forceSave가 만든 draft가 지연 setThread로 [초안]으로 그려지는 것을 막는다.
        // 진입 시점(getMailData 이전)에 동기 등록하고, 실패·타임아웃 시 해제한다(성공 시 유지 — 경쟁 창 보호).
        if (sendingDraftId != null) {
          this.dataMgr.sendingEmailIds.add(sendingDraftId)
        }

        const resultUI: ResultUI = showResult({
          loading: true,
          closeOnClick: false,
          message: i18n.t('email.result.sending'),
        })

        const closeResultUI = () => {
          try {
            resultUI.close()
          } catch (err) {
            console.error(' - closeResultUI error - ', err)
          }
          this.isSending = false
        }

        const callSended = ({
          mailData,
          prevDraft,
        }: {
          mailData: EmailData
          prevDraft: EmailObj | null
        }) => {
          try {
            if (onSended != null) {
              onSended({ mailData, prevDraft })
            }
          } catch (err) {
            console.error(' - onSended error - ', err)
          } finally {
            closeResultUI()
            this.sendPromiseMgr.resolveAll({ mailData, prevDraft })
          }
        }

        const callError = (err: Error | string) => {
          // 발송 실패: draft로 되돌아가므로 "발송 중" 표시 해제 → [초안] 다시 표시.
          if (sendingDraftId != null) {
            this.dataMgr.sendingEmailIds.delete(sendingDraftId)
          }
          try {
            closeResultUI()
            if (onError != null) {
              onError(err)
            }
          } catch (e) {
            console.error(' - send callError internal error - ', e)
          } finally {
            this.sendPromiseMgr.rejectAll(err)
          }
        }

        if (this.formAPIs != null) {
          try {
            const formData = this.formAPIs.getEmailValues(false)

            this.getMailData(formData)
              .then(async (mailData) => {
                console.log('send mailData - ', mailData)

                const { to, cc, bcc } = mailData

                const mailList = [...(to || []), ...(cc || []), ...(bcc || [])]
                const contactList = await Promise.all(
                  mailList.map(async (item) => {
                    const email = typeof item === 'string' ? item : (item as EmailInfo).email
                    if (email != null && typeof email === 'string' && email.trim().length > 0) {
                      let contactInfo = await getContactInfo(email)
                      if (contactInfo == null) {
                        contactInfo = {
                          id: email,
                          name: typeof item === 'string' ? email : (item as EmailInfo).name,
                          email,
                          avatarUrl: null,
                        }
                        this.dataMgr.addTempEmail({
                          email,
                          name: typeof item === 'string' ? email : (item as EmailInfo).name || '',
                        })
                      }
                      return contactInfo
                    }
                    return null
                  }),
                )

                console.log(' - contactList - ', contactList)

                // 메일 전송 시 최근 이메일 주소 목록에 추가.
                this.dataMgr.addRecentSendEmails(contactList)

                await this.createEmail(mailData, curDraft, true)
                  .then(() => {
                    this.isSended = true

                    callSended({ mailData, prevDraft: curDraft })
                  })
                  .catch((err) => {
                    // "발송 중" 해제는 callError에서 일괄 처리(모든 실패 경로 공통).
                    this.saveToOutbox(formData, curDraft).catch((e) => {
                      console.error('[SendHelper] saveToOutbox in send error handler failed:', e)
                    })
                    callError(err)
                  })
              })
              .catch((err) => {
                console.log(' - getMailData error - ', err)
                callError(err)
              })
          } catch (err) {
            callError(err instanceof Error ? err : new Error(String(err)))
          }
        } else {
          callError('formAPIs is null')
        }
      }
    })
  }

  sendScheduled = async (
    scheduledDateTime: dayjs.Dayjs,
    options: SendScheduledOptions = {},
  ): Promise<any> => {
    let errorMessage: string | null = null
    this.isSendCalled = true

    if (this.isSaving === true) {
      // [fix] 저장중이면 저장 후 호출. save가 hang되면 무한 대기하는 문제 방지. 30초 후 타임아웃.
      const SAVE_WAIT_TIMEOUT_MS = 30_000
      let saveTimeoutId: ReturnType<typeof setTimeout>
      const saveTimeoutPromise = new Promise<any>((_, reject) => {
        saveTimeoutId = setTimeout(
          () => reject(new Error(i18n.t('email.result.saveTimeout'))),
          SAVE_WAIT_TIMEOUT_MS,
        )
      })
      try {
        await Promise.race([this.save(), saveTimeoutPromise])
      } catch (err) {
        console.error(' - sendScheduled: save wait failed - ', err)
        this.isSaving = false
      } finally {
        clearTimeout(saveTimeoutId!)
      }
      return this.sendScheduled(scheduledDateTime, options)
    }

    return new Promise((resolve, reject) => {
      // [fix] JMAP 요청이 hang되면 sendPromiseMgr가 settle되지 않는 문제 방지 (예약 발송). 120초 후 강제 reject.
      const SEND_OPERATION_TIMEOUT_MS = 120_000
      const sendTimeoutId = setTimeout(() => {
        this.isSending = false
        this.sendPromiseMgr.rejectAll(new Error(i18n.t('email.result.sendTimeout')))
      }, SEND_OPERATION_TIMEOUT_MS)

      this.sendPromiseMgr.add(
        (data) => {
          clearTimeout(sendTimeoutId)
          resolve(data)
        },
        (err) => {
          clearTimeout(sendTimeoutId)
          reject(err)
        },
      )

      if (this.isSending !== true) {
        this.isSending = true
        const { onSended, onError } = this.options

        const resultUI: ResultUI = showResult({
          loading: true,
          closeOnClick: false,
          message: i18n.t('email.result.schedulingSend'),
        })

        const closeResultUI = () => {
          try {
            resultUI.close()
          } catch (err) {
            console.error(' - closeResultUI error - ', err)
          }
          this.isSending = false
        }

        const callSended = ({
          mailData,
          prevDraft,
        }: {
          mailData: EmailData
          prevDraft: EmailObj | null
        }) => {
          try {
            if (onSended != null) {
              onSended({ mailData, prevDraft })
            }
          } catch (err) {
            console.error(' - scheduled onSended error - ', err)
          } finally {
            closeResultUI()
            this.sendPromiseMgr.resolveAll({ mailData, prevDraft })
          }
        }

        const callError = (err: Error | string) => {
          try {
            closeResultUI()
            if (onError != null) {
              onError(err)
            }
          } catch (e) {
            console.error(' - scheduled callError internal error - ', e)
          } finally {
            this.sendPromiseMgr.rejectAll(err)
          }
        }

        if (this.formAPIs != null) {
          const formData = this.formAPIs.getEmailValues(false)

          this.getMailData(formData)
            .then(async (mailData) => {
              console.log('send mailData - ', mailData)

              const { to, cc, bcc } = mailData

              const mailList = [...(to || []), ...(cc || []), ...(bcc || [])]
              const contactList = await Promise.all(
                mailList.map(async (item) => {
                  const email = typeof item === 'string' ? item : (item as EmailInfo).email
                  if (email != null && typeof email === 'string' && email.trim().length > 0) {
                    let contactInfo = await getContactInfo(email)
                    if (contactInfo == null) {
                      contactInfo = {
                        id: email,
                        name: typeof item === 'string' ? email : (item as EmailInfo).name,
                        email,
                        avatarUrl: null,
                      }
                      this.dataMgr.addTempEmail({
                        email,
                        name: typeof item === 'string' ? email : (item as EmailInfo).name || '',
                      })
                    }
                    return contactInfo
                  }
                  return null
                }),
              )

              console.log(' - contactList - ', contactList)

              // 메일 전송 시 최근 이메일 주소 목록에 추가.
              this.dataMgr.addRecentSendEmails(contactList)

              // OMD-204: 원본 formData 대신 getMailData가 정제한 mailData를 EML 입력으로 사용한다.
              // (formData의 to/cc는 getMailData 정제를 우회해 EML 헤더에 비가시 문자가 남는 사각지대였음)
              const emailData = mailData

              // 2. EmailObj를 사용하여 EML 생성 (회신 시 스레딩 헤더 포함)
              const emlContent = await generateEMLUsingEmailObj(
                emailData,
                true,
                this.getThreadingFields(),
              )

              // 3. EML 유효성 검증
              const validationResult: ValidationResult = validateEMLContent(emlContent, emailData)

              // 4. 개발 모드에서 상세 로깅
              if (import.meta.env.DEV) {
                console.group('📧 Schedule Send - EML Generation (via EmailObj)')
                console.log('Email Data:', emailData)
                console.log('Email Data Attachments:', emailData.attachments)
                emailData.attachments?.forEach((attachment, index) => {
                  console.log(`EmailData Attachment ${index}:`, attachment)
                  console.log(`  - file:`, attachment.file)
                  console.log(`  - originFile:`, attachment.originFile)
                })
                console.log('Generated EML Content:', emlContent)
                console.log('Validation Result:', validationResult)
                console.log('Scheduled Time (UTC):', scheduledDateTime.utc().format())
                console.log('EML File Size:', new Blob([emlContent]).size, 'bytes')
                console.groupEnd()
              }

              // 5. 유효성 검증 실패 시 경고 (치명적 오류가 아닌 경우 계속 진행)
              if (!validationResult.isValid && validationResult.errors.length > 0) {
                console.warn('EML validation errors found:', validationResult.errors)
                // 치명적 오류가 있는 경우만 중단 (예: 필수 헤더 누락)
                const criticalErrors = validationResult.errors.filter(
                  (error) =>
                    error.includes('Missing required header') ||
                    error.includes('No recipient headers'),
                )
                if (criticalErrors.length > 0) {
                  errorMessage =
                    'Critical EML validation errors found. Please check the email content.'
                  callError(new Error(errorMessage))
                  return
                }
              }

              // 6. EML 파일 생성
              const emlFile = new File([emlContent], 'scheduled_email.eml', {
                type: 'message/rfc822',
              })

              // 7. API 파라미터 검증
              const scheduledTimeString = scheduledDateTime.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')

              // attachment의 총 size를 계산.
              let attachmentsSize = 0
              emailData.attachments?.forEach((attachment) => {
                attachmentsSize += attachment.file.size
              })

              if (attachmentsSize > this.attachmentsLimit) {
                const limit = formatBytes(this.attachmentsLimit, 1, false, 2)
                const size = formatBytes(attachmentsSize, 1, false, 2)
                errorMessage = i18n.t('email.error.attachmentsSizeLimit', { size, limit })
                callError(new Error(errorMessage))
                return
              }

              // 예약 시간 재검증 (API 호출 직전)
              if (scheduledDateTime.isBefore(dayjs())) {
                errorMessage = i18n.t('email.error.scheduledTimePassed')
                callError(new Error(errorMessage))
                return
              }

              // 8. send_mode / secure_options 결정 (옵션 기반)
              const { secureSendEnabled, secureExpiresAt, readReceiptEnabled } = options
              let sendMode: ScheduleSendMode = 'normal'
              let secureOptions: SecureOptions | undefined
              if (secureSendEnabled === true && secureExpiresAt != null) {
                sendMode = readReceiptEnabled === true ? 'secure' : 'secure_no_read_receipt'
                secureOptions = { expires_at: secureExpiresAt }
              } else if (readReceiptEnabled === true) {
                sendMode = 'read_receipt'
              }

              // 9. 예약 발송 API 호출
              const apiParams: {
                scheduled_time: string
                eml_file: File
                snippet: string
                send_mode: ScheduleSendMode
                secure_options?: SecureOptions
                bcc?: { email: string; name: string }[]
              } = {
                scheduled_time: scheduledTimeString,
                eml_file: emlFile,
                snippet: emailData.htmlText?.substring(0, 128) || '',
                send_mode: sendMode,
              }
              if (secureOptions != null) {
                apiParams.secure_options = secureOptions
              }

              // BCC 수신자는 서버에 { email, name } 객체 배열로 별도 전달한다.
              const toRecipientObjects = (list?: (EmailInfo | string)[]) =>
                (list || [])
                  .map((item) =>
                    typeof item === 'string'
                      ? { email: item, name: '' }
                      : { email: item.email, name: item.name ?? '' },
                  )
                  .filter((recipient) => recipient.email.trim().length > 0)

              // eml 파일 내에서는 cc만 포함되고, bcc는 제거하기 떄문에 API 호출 시 bcc는 아래와 같이 추가한다.
              const bccAddresses = toRecipientObjects(bcc)
              if (bccAddresses.length > 0) {
                apiParams.bcc = bccAddresses
              }

              // API 호출 전 상세 로깅
              if (import.meta.env.DEV) {
                console.group('📤 Schedule Send API Call')
                console.log('API Parameters:', {
                  scheduled_time: apiParams.scheduled_time,
                  eml_file_name: apiParams.eml_file.name,
                  eml_file_size: apiParams.eml_file.size,
                  eml_file_type: apiParams.eml_file.type,
                  snippet: apiParams.snippet,
                })
                console.log('EML File Content Preview:', emlContent.substring(0, 200) + '...')
                console.groupEnd()
              }

              await sendScheduledEmail(apiParams)
                .then(() => {
                  this.isSended = true

                  callSended({ mailData, prevDraft: null })
                })
                .catch((err: any) => {
                  console.log(' - send error - ', err)
                  // 사용자에게 더 구체적인 에러 메시지 제공
                  if (err.message === 'Scheduled time must be greater than now + 60 seconds') {
                    errorMessage = i18n.t('scheduleSend.error.scheduledTimeTooSoon')
                  } else {
                    errorMessage = err.message || i18n.t('email.error.sendFailed')
                  }
                  if (err.response) {
                    const status = err.response.status
                    if (status === 400) {
                      errorMessage = i18n.t('email.error.invalidData')
                    } else if (status === 401) {
                      errorMessage = i18n.t('email.error.authFailed')
                    } else if (status === 422) {
                      errorMessage = i18n.t('email.error.scheduledInvalidFormat')
                    } else if (status >= 500) {
                      errorMessage = i18n.t('email.error.serverError')
                    }
                  }
                  callError(new Error(errorMessage))
                  return
                })
            })
            .catch((err) => {
              console.log(' - getMailData error - ', err)
              callError(err)
              console.error('Schedule send failed:', err)

              // 상세한 에러 로깅
              if (import.meta.env.DEV) {
                console.group('❌ Schedule Send Error Details')
                console.error('Error object:', err)
                console.error('Error message:', err.message)
                console.error('Error stack:', err.stack)
                if (err.response) {
                  console.error('API Response Status:', err.response.status)
                  console.error('API Response Data:', err.response.data)
                  console.error('API Response Headers:', err.response.headers)
                }
                console.groupEnd()
              }
            })
        } else {
          callError('formAPIs is null')
        }
      }
    })
  }

  save = async (forceSave?: boolean, forSave?: boolean): Promise<any> => {
    if (this.isSending === true) {
      // 발송 중이면 저장하지 않는다.
      return Promise.resolve(null)
    }

    return new Promise((resolve, reject) => {
      this.savePromiseMgr.add(resolve, reject)

      if (this.isSaving !== true) {
        this.isSaving = true
        const { onSaved, onError } = this.options

        let curDraft = this.curDraft

        const resultUI: ResultUI = showResult({
          loading: true,
          closeOnClick: false,
          message: i18n.t('email.result.saving'),
        })

        // [fix] save() 자체에 timeout이 없어 createEmail() hang 시 영구 대기하는 문제 방지
        const SAVE_OPERATION_TIMEOUT_MS = 120_000
        let saveOperationTimeoutId: ReturnType<typeof setTimeout> | undefined

        const closeResultUI = () => {
          clearTimeout(saveOperationTimeoutId)
          try {
            resultUI.close()
          } catch (err) {
            console.error(' - closeResultUI error - ', err)
          }
          this.isSaving = false
        }

        const callSaved = ({
          mailData,
          prevDraft,
          draft,
        }: {
          mailData: EmailData
          prevDraft: EmailObj | null
          draft: EmailObj
        }) => {
          const isChanged = prevDraft !== draft
          try {
            if (onSaved != null) {
              onSaved({ mailData, prevDraft, draft, isChanged })
            }
          } catch (err) {
            console.error(' - onSaved error - ', err)
          } finally {
            closeResultUI()
            this.savePromiseMgr.resolveAll({ mailData, prevDraft, draft, isChanged })
          }
        }

        const callError = (err: Error | string) => {
          try {
            closeResultUI()
            if (onError != null) {
              onError(err)
            }
          } catch (e) {
            console.error(' - callError internal error - ', e)
          } finally {
            this.savePromiseMgr.rejectAll(err)
          }
        }

        saveOperationTimeoutId = setTimeout(() => {
          console.error('[SendHelper] save() timed out after 120s - forcing callError')
          callError(new Error(i18n.t('email.result.saveTimeout')))
        }, SAVE_OPERATION_TIMEOUT_MS)

        if (
          this.formAPIs != null &&
          (forceSave === true ||
            curDraft != null ||
            (curDraft == null && this.formAPIs.isEmpty() !== true))
        ) {
          try {
            if (curDraft != null) {
              // draft가 삭제된 경우에는 새로 draft를 생성.
              const draftData = curDraft.data
              if (draftData == null) {
                const msg = 'save: curDraft.data is null/undefined'
                console.error(` - ${msg} - `, {
                  curDraft,
                  curDraftKeys: Object.keys(curDraft),
                })
                if (import.meta.env.DEV) {
                  window.alert(
                    `[SendHelper] ${msg}\ncurDraftKeys: ${Object.keys(curDraft).join(', ')}`,
                  )
                }
                curDraft = null
              } else {
                const email = dataManager.getEmailById(draftData.id)
                if (email == null) {
                  curDraft = null
                } else if (email.data == null || email.data.mailboxIds == null) {
                  const msg = 'save: email data is corrupted'
                  console.error(` - ${msg} - `, {
                    emailId: draftData.id,
                    emailData: email.data,
                  })
                  if (import.meta.env.DEV) {
                    window.alert(
                      `[SendHelper] ${msg}\nemailId: ${draftData.id}\nemail.data: ${JSON.stringify(email.data)}`,
                    )
                  }
                  curDraft = null
                } else if (Object.keys(email.data.mailboxIds).length === 0) {
                  curDraft = null
                }
              }
            }

            const formData = this.formAPIs.getEmailValues(forSave)

            this.getMailData(formData)
              .then(async (mailData) => {
                if (
                  forceSave !== true &&
                  curDraft != null &&
                  this.formAPIs!.isModified() !== true
                ) {
                  console.log(' - save - no change - ', curDraft)

                  callSaved({ mailData, prevDraft: curDraft, draft: curDraft })
                  return
                }

                await this.createEmail(mailData, curDraft, false)
                  .then((ret) => {
                    this.isSended = true

                    if (ret.test === true) {
                      const prevDraft = curDraft
                      const draft = curDraft!
                      callSaved({ mailData, prevDraft, draft })
                      return
                    }

                    const { createdEmail } = ret

                    const { created } = createdEmail

                    if (created == null) {
                      callError('save error - no email created')
                      return
                    }

                    const createdIds = Object.keys(created)
                      .map((key) => {
                        return created[key].id
                      })
                      .filter((id) => {
                        return id != null
                      })

                    if (createdIds.length > 1) {
                      callError('save error - multiple email created')
                      return
                    }

                    const emailId = createdIds[0]

                    if (emailId == null) {
                      callError(`save error - ${createdEmail.notCreated}`)
                      return
                    }

                    const savedEmail = this.dataMgr.getEmailById(emailId)
                    if (savedEmail != null) {
                      // [fix] send()의 30초 타임아웃이 이미 발동한 경우 curDraft 업데이트 건너뜀
                      if (this.isSaving !== true) {
                        this.savePromiseMgr.resolveAll(null)
                        return
                      }
                      const prevDraft = curDraft
                      this.curDraft = savedEmail
                      const draft = this.curDraft

                      this.applySavedData(savedEmail, mailData, prevDraft)

                      callSaved({ mailData, prevDraft, draft })
                    } else {
                      return this.dataMgr
                        .applyEmailById(emailId, true)
                        .then((email) => {
                          // [fix] send()의 30초 타임아웃이 이미 발동한 경우 curDraft 업데이트 건너뜀
                          if (this.isSaving !== true) {
                            this.savePromiseMgr.resolveAll(null)
                            return
                          }
                          const prevDraft = curDraft
                          this.curDraft = email
                          const draft = this.curDraft

                          this.applySavedData(email, mailData, prevDraft)

                          callSaved({ mailData, prevDraft, draft })
                        })
                        .catch((err) => {
                          console.log(' - load error - ', err)
                          callError(err)
                        })
                    }
                  })
                  .catch((err) => {
                    console.error(' - save error - ', err)
                    callError(err)
                  })
              })
              .catch((err) => {
                console.error(' - getMailData error - ', err)
                callError(err)
              })
          } catch (err) {
            if (err instanceof Error) {
              callError(err) // 안전하게 접근 가능
            } else {
              callError(String(err))
            }
          }
        } else {
          callError('formAPIs is null')
        }
      }
    })
  }

  applySavedData = (emailObj: EmailObj, mailData: EmailData, prevDraft: EmailObj | null): void => {
    console.log(' - applySavedData : ', emailObj, mailData, prevDraft)

    const viewInfo = getThreadViewInfo()
    const { viewerWindow } = viewInfo

    if (viewerWindow.webObj != null && prevDraft != null) {
      const emailIds = viewerWindow.webObj.getEmailIds()
      const emailId = prevDraft.data.id

      const pos = emailIds.indexOf(emailId)

      if (pos > -1) {
        // 이전의 draft가 현재 threadViewer에 표시된 이메일 목록에 포함된 경우에만 replace 처리.
        // 최초의 draft ID를 유지하여 EmailThreadViewer에서 변경된 draft를 그대로 유지하여 표시할 수 있도록 함.
        const { htmlBody, attachments } = mailData

        const bodyData = {
          type: 'text/html',
          body: htmlBody,
          attachments: (attachments || []).map((attachment, idx) => {
            const temp = attachmentJSONHelper.createAttachment(
              attachment.file as any,
              attachment.file.cid || null,
              idx,
            )
            return temp.dataForJSON
          }),
        }

        const emailInfo = emailHelper.createEmailInfoFromEmail(emailObj as any, true, null, false)
        if (emailInfo != null) {
          ;(emailInfo.dataForJSON as any).body = bodyData
          const emailDataStr = JSON.stringify(emailInfo.dataForJSON)

          viewerWindow.webObj.deleteEmail(emailId)
          // NativeToWeb.addEmail 은 emailInfoStr 1개만 받는다(위치 인자 없음). 기존 2번째 인자
          // (nextEmailId)는 런타임에서 전달·사용된 적이 없어(정밀 타이핑으로 드러남) 제거한다.
          viewerWindow.webObj.addEmail(emailDataStr)
        }
      }
    }
  }

  resetCalledState = (): void => {
    this.isSended = false
    this.isSendCalled = false
    this.isDeleteCalled = false
  }

  deleteDraft = (): Promise<any> | null => {
    const { curDraft } = this

    this.isDeleteCalled = true

    if (this.formAPIs != null) {
      const formData = this.formAPIs.getEmailValues()
      const { attachments, sharingAttachments } = formData
      const allAttachment = [...(attachments || []), ...(sharingAttachments || [])]
      allAttachment.forEach((attachment) => {
        if (attachment.isUploading) {
          attachment.delete()
        }
      })
    }

    if (curDraft != null && curDraft.data.mailboxIds != null) {
      let mailbox: any = null
      const mailboxIds = Object.keys(curDraft.data.mailboxIds)
      if (mailboxIds.length > 0) {
        mailbox = dataManager.getMailboxById(mailboxIds[0])
      }
      if (mailbox == null) {
        mailbox = dataManager.getMailboxForRole('drafts')
      }
      if (mailbox != null) {
        return mailbox.deleteEmails([curDraft])
      }
    }

    return null
  }

  isNewEmail = (): boolean => {
    return this.options.prevEmail == null
  }

  getUserInfo = (): any => {
    return this.userInfo
  }

  getPrevEmail = (): EmailObj | null => {
    return this.options.prevEmail || null
  }

  /**
   * 발송에 실을 스레딩 헤더 값을 계산한다. 순수 로직은 `computeThreadingFields`.
   *
   * - 회신(reply/replyAll): In-Reply-To + References.
   * - 전달(forward): 회신이 아니므로 In-Reply-To 없이 References 로만 스레드를 잇는다(in_reply_to 부재).
   * - 신규·재전송 등 그 외에는 null 을 반환해 스레딩 위장 주입을 막는다.
   * - 꺾쇠(`<>`) 유무는 가정하지 않는다. JSON 발송 경로는 원본 값을 그대로 받아 서버가
   *   정규화하고, EML 경로는 `buildThreadingHeaders` 가 꺾쇠를 보정한다.
   */
  private getThreadingFields = (): ThreadingFields | null =>
    computeThreadingFields(this.cmd, this.getPrevEmail()?.data)

  getDraftEmail = (): EmailObj | null => {
    return this.curDraft
  }

  getExtValues = (): any => {
    return this.options.extValues
  }

  notifyTitleChanged = (title: string): void => {
    console.log(' - notifyTitleChanged - ', title)
    if (this.options != null && this.options.onTitleChanged != null) {
      this.options.onTitleChanged(title)
    }
  }

  // draft에 대해 reqDetail()을 호출해 bodyValues를 가져온다.
  // sendReadReceipt / sendSecure에서 draft 재로드 첨부의 base64 변환 시
  // partId → bodyValues lookup 경로에 사용한다.
  private getDraftBodyValues = async (): Promise<Record<string, { value: string }> | null> => {
    const draft = this.curDraft
    if (draft == null || typeof draft.reqDetail !== 'function') {
      return null
    }
    try {
      const detail = await draft.reqDetail()
      const bodyValues = detail?.bodyValues
      if (bodyValues != null && typeof bodyValues === 'object') {
        return bodyValues as Record<string, { value: string }>
      }
      return null
    } catch (err) {
      console.warn('[SendHelper] getDraftBodyValues failed', err)
      return null
    }
  }

  sendSecure = async (expireDate: string, readReceiptEnabled: boolean): Promise<any> => {
    this.isSendCalled = true

    if (this.isSaving === true) {
      const SAVE_WAIT_TIMEOUT_MS = 30_000
      let saveTimeoutId: ReturnType<typeof setTimeout>
      const saveTimeoutPromise = new Promise<any>((_, reject) => {
        saveTimeoutId = setTimeout(
          () => reject(new Error(i18n.t('email.result.saveTimeout'))),
          SAVE_WAIT_TIMEOUT_MS,
        )
      })
      try {
        await Promise.race([this.save(), saveTimeoutPromise])
      } catch (err) {
        console.error(' - sendSecure: save wait failed - ', err)
        this.isSaving = false
      } finally {
        clearTimeout(saveTimeoutId!)
      }
      return this.sendSecure(expireDate, readReceiptEnabled)
    }

    return new Promise((resolve, reject) => {
      // 발송 중 draft id. 진입 시 등록, 실패·타임아웃 시 해제(타임아웃=발송 포기 → [초안] 복원).
      const sendingDraftId =
        this.curDraft != null && this.curDraft.data != null ? this.curDraft.data.id : null

      const SEND_OPERATION_TIMEOUT_MS = 120_000
      const sendTimeoutId = setTimeout(() => {
        this.isSending = false
        if (sendingDraftId != null) {
          this.dataMgr.sendingEmailIds.delete(sendingDraftId)
        }
        this.sendPromiseMgr.rejectAll(new Error(i18n.t('email.result.sendTimeout')))
      }, SEND_OPERATION_TIMEOUT_MS)

      this.sendPromiseMgr.add(
        (data) => {
          clearTimeout(sendTimeoutId)
          resolve(data)
        },
        (err) => {
          clearTimeout(sendTimeoutId)
          reject(err)
        },
      )

      if (this.isSending !== true) {
        this.isSending = true
        const { onSended, onError } = this.options

        const resultUI: ResultUI = showResult({
          loading: true,
          closeOnClick: false,
          message: i18n.t('email.result.sending'),
        })

        const closeResultUI = () => {
          try {
            resultUI.close()
          } catch (err) {
            console.error(' - closeResultUI error - ', err)
          }
          this.isSending = false
        }

        const callSended = ({
          mailData,
          prevDraft,
        }: {
          mailData: EmailData
          prevDraft: EmailObj | null
        }) => {
          try {
            if (onSended != null) {
              onSended({ mailData, prevDraft })
            }
          } catch (err) {
            console.error(' - sendSecure onSended error - ', err)
          } finally {
            closeResultUI()
            this.sendPromiseMgr.resolveAll({ mailData, prevDraft })
          }
        }

        // 발송 시작 시 draft id를 "발송 중" 집합에 등록(viewer [초안] 차단). send()와 동일 패턴.
        // 보안메일도 발송 직전 forceSave(EmailForm)로 draft가 생성되어 같은 지연 setThread 경쟁이
        // 발생하므로 가드가 필요하다. 실패·타임아웃 시 해제(성공 시 EmailForm이 deleteDraft로 정리).
        if (sendingDraftId != null) {
          this.dataMgr.sendingEmailIds.add(sendingDraftId)
        }

        const callError = (err: Error | string) => {
          // 발송 실패: draft로 되돌아가므로 "발송 중" 표시 해제 → [초안] 다시 표시.
          if (sendingDraftId != null) {
            this.dataMgr.sendingEmailIds.delete(sendingDraftId)
          }
          try {
            closeResultUI()
            if (onError != null) {
              onError(err)
            }
          } catch (e) {
            console.error(' - sendSecure callError internal error - ', e)
          } finally {
            this.sendPromiseMgr.rejectAll(err)
          }
        }

        if (this.formAPIs != null) {
          const formData = this.formAPIs.getEmailValues(false)

          this.getMailData(formData)
            .then(async (mailData) => {
              console.log('sendSecure mailData - ', mailData)

              const { to, cc, bcc } = mailData

              const mailList = [...(to || []), ...(cc || []), ...(bcc || [])]
              const contactList = await Promise.all(
                mailList.map(async (item) => {
                  const email = typeof item === 'string' ? item : (item as EmailInfo).email
                  if (email != null && typeof email === 'string' && email.trim().length > 0) {
                    let contactInfo = await getContactInfo(email)
                    if (contactInfo == null) {
                      contactInfo = {
                        id: email,
                        name: typeof item === 'string' ? email : (item as EmailInfo).name,
                        email,
                        avatarUrl: null,
                      }
                      this.dataMgr.addTempEmail({
                        email,
                        name: typeof item === 'string' ? email : (item as EmailInfo).name || '',
                      })
                    }
                    return contactInfo
                  }
                  return null
                }),
              )

              this.dataMgr.addRecentSendEmails(contactList)

              const toRecipientList = (list?: (EmailInfo | string)[]) =>
                (list || []).map((item) => {
                  if (typeof item === 'string') return { email: item }
                  return { email: item.email, name: item.name }
                })

              const fromList = mailData.from || []
              const { senderEmail, senderName } = (() => {
                if (fromList.length === 0) return { senderEmail: '', senderName: '' }
                const first = fromList[0]
                if (typeof first === 'string') return { senderEmail: first, senderName: '' }
                const { name: name0, email } = first as EmailInfo
                if (name0 == null || name0.trim().length === 0)
                  return { senderEmail: email, senderName: '' }
                const name = name0.trim()
                return { senderEmail: email, senderName: name }
              })()

              const bodyValues = await this.getDraftBodyValues()
              const attachmentItems: AttachmentItem[] = await buildBase64Attachments(
                mailData.attachments || [],
                bodyValues,
              )

              const apiParams: SendSecureMailData = {
                sender_email: senderEmail,
                sender_name: senderName,
                recipient_list: toRecipientList(to),
                cc_list: toRecipientList(cc),
                bcc_list: toRecipientList(bcc),
                subject: mailData.subject || '',
                body_html: mailData.htmlBody || '',
                options: { expires_at: expireDate },
                // options: { expires_at: new Date(Date.now() + 1 * 60 * 1000).toISOString() },
                secure_send_enabled: true,
                read_receipt: readReceiptEnabled,
              }

              if (attachmentItems.length > 0) {
                apiParams.attachments = attachmentItems
              }

              const threading = this.getThreadingFields()
              if (threading != null) {
                // forward 는 in_reply_to 가 없고 References 로만 스레드를 잇는다.
                if (threading.in_reply_to != null) {
                  apiParams.in_reply_to = threading.in_reply_to
                }
                apiParams.references = threading.references
              }

              if (import.meta.env.DEV) {
                console.group('🔒 Secure Send API Call')
                console.log('API Parameters:', apiParams)
                console.groupEnd()
              }

              await secureSendApiHelper
                .sendSecureMail(apiParams)
                .then(() => {
                  this.isSended = true
                  callSended({ mailData, prevDraft: null })
                })
                .catch((err: any) => {
                  console.error(' - sendSecure error - ', err)
                  let errorMessage = err.message || i18n.t('email.error.sendFailed')
                  if (err.response) {
                    const status = err.response.status
                    if (status === 400) {
                      errorMessage = i18n.t('email.error.invalidData')
                    } else if (status === 401) {
                      errorMessage = i18n.t('email.error.authFailed')
                    } else if (status >= 500) {
                      errorMessage = i18n.t('email.error.serverError')
                    }
                  }
                  callError(new Error(errorMessage))
                })
            })
            .catch((err) => {
              console.error('sendSecure getMailData failed:', err)
              callError(err)
            })
        } else {
          callError('formAPIs is null')
        }
      }
    })
  }

  sendReadReceipt = async (): Promise<any> => {
    this.isSendCalled = true

    if (this.isSaving === true) {
      // save() 자체에 120s timeout 내장 (L1064-1067) — 외부 race + isSaving 강제 변경 + 재귀
      // 패턴은 race condition을 유발하므로 제거하고 piggyback으로 단일화.
      // save 실패해도 send는 시도 (수신확인은 별도 마이크로서비스 API라 가능 — 기존 동작 유지).
      try {
        await this.save()
      } catch (err) {
        console.error(' - sendReadReceipt: save wait failed - ', err)
      }
    }

    return new Promise((resolve, reject) => {
      // 발송 중 draft id. 진입 시 등록, 실패·타임아웃 시 해제(타임아웃=발송 포기 → [초안] 복원).
      const sendingDraftId =
        this.curDraft != null && this.curDraft.data != null ? this.curDraft.data.id : null

      const SEND_OPERATION_TIMEOUT_MS = 120_000
      const sendTimeoutId = setTimeout(() => {
        this.isSending = false
        if (sendingDraftId != null) {
          this.dataMgr.sendingEmailIds.delete(sendingDraftId)
        }
        this.sendPromiseMgr.rejectAll(new Error(i18n.t('email.result.sendTimeout')))
      }, SEND_OPERATION_TIMEOUT_MS)

      this.sendPromiseMgr.add(
        (data) => {
          clearTimeout(sendTimeoutId)
          resolve(data)
        },
        (err) => {
          clearTimeout(sendTimeoutId)
          reject(err)
        },
      )

      if (this.isSending !== true) {
        this.isSending = true
        const { onSended, onError } = this.options

        const resultUI: ResultUI = showResult({
          loading: true,
          closeOnClick: false,
          message: i18n.t('email.result.sending'),
        })

        const closeResultUI = () => {
          try {
            resultUI.close()
          } catch (err) {
            console.error(' - closeResultUI error - ', err)
          }
          this.isSending = false
        }

        const callSended = ({
          mailData,
          prevDraft,
        }: {
          mailData: EmailData
          prevDraft: EmailObj | null
        }) => {
          try {
            if (onSended != null) {
              onSended({ mailData, prevDraft })
            }
          } catch (err) {
            console.error(' - sendReadReceipt onSended error - ', err)
          } finally {
            closeResultUI()
            this.sendPromiseMgr.resolveAll({ mailData, prevDraft })
          }
        }

        // 발송 시작 시 draft id를 "발송 중" 집합에 등록(viewer [초안] 차단). send()와 동일 패턴.
        // 수신확인메일도 발송 직전 forceSave(EmailForm)로 draft가 생성되어 같은 지연 setThread 경쟁이
        // 발생하므로 가드가 필요하다. 실패·타임아웃 시 해제(성공 시 EmailForm이 deleteDraft로 정리).
        if (sendingDraftId != null) {
          this.dataMgr.sendingEmailIds.add(sendingDraftId)
        }

        const callError = (err: Error | string) => {
          // 발송 실패: draft로 되돌아가므로 "발송 중" 표시 해제 → [초안] 다시 표시.
          if (sendingDraftId != null) {
            this.dataMgr.sendingEmailIds.delete(sendingDraftId)
          }
          try {
            closeResultUI()
            if (onError != null) {
              onError(err)
            }
          } catch (e) {
            console.error(' - sendReadReceipt callError internal error - ', e)
          } finally {
            this.sendPromiseMgr.rejectAll(err)
          }
        }

        if (this.formAPIs != null) {
          const formData = this.formAPIs.getEmailValues(false)

          this.getMailData(formData)
            .then(async (mailData) => {
              const { to, cc, bcc } = mailData

              const mailList = [...(to || []), ...(cc || []), ...(bcc || [])]
              const contactList = await Promise.all(
                mailList.map(async (item) => {
                  const email = typeof item === 'string' ? item : (item as EmailInfo).email
                  if (email != null && typeof email === 'string' && email.trim().length > 0) {
                    let contactInfo = await getContactInfo(email)
                    if (contactInfo == null) {
                      contactInfo = {
                        id: email,
                        name: typeof item === 'string' ? email : (item as EmailInfo).name,
                        email,
                        avatarUrl: null,
                      }
                      this.dataMgr.addTempEmail({
                        email,
                        name: typeof item === 'string' ? email : (item as EmailInfo).name || '',
                      })
                    }
                    return contactInfo
                  }
                  return null
                }),
              )

              this.dataMgr.addRecentSendEmails(contactList)

              const toRrEmailAddressList = (list?: (EmailInfo | string)[]) =>
                (list || []).map((item) => {
                  if (typeof item === 'string') return { email: item }
                  return { email: item.email, display_name: item.name }
                })

              const fromList = mailData.from || []
              const sender = (() => {
                if (fromList.length === 0) return { email: '' }
                const first = fromList[0]
                if (typeof first === 'string') return { email: first }
                const { name, email } = first as EmailInfo
                if (name == null || name.trim().length === 0) return { email }
                return { email, display_name: name.trim() }
              })()

              const bodyValues = await this.getDraftBodyValues()
              const attachmentItems = await buildBase64Attachments(
                mailData.attachments || [],
                bodyValues,
              )

              const apiParams: SendRequest = {
                sender,
                to: toRrEmailAddressList(to),
                cc: toRrEmailAddressList(cc),
                bcc: toRrEmailAddressList(bcc),
                subject: mailData.subject || '',
                html_body: mailData.htmlBody || '',
              }

              if (attachmentItems.length > 0) {
                apiParams.attachments = attachmentItems
              }

              const threading = this.getThreadingFields()
              if (threading != null) {
                // forward 는 in_reply_to 가 없고 References 로만 스레드를 잇는다.
                if (threading.in_reply_to != null) {
                  apiParams.in_reply_to = threading.in_reply_to
                }
                apiParams.references = threading.references
              }

              if (import.meta.env.DEV) {
                console.group('📨 Read Receipt Send API Call')
                console.log('API Parameters:', apiParams)
                console.groupEnd()
              }

              await readReceiptApiHelper
                .sendMail(apiParams)
                .then(() => {
                  this.isSended = true
                  callSended({ mailData, prevDraft: null })
                })
                .catch((err: any) => {
                  console.error(' - sendReadReceipt error - ', err)
                  let errorMessage = err.message || i18n.t('email.error.sendFailed')
                  if (err.response) {
                    const status = err.response.status
                    if (status === 400) {
                      errorMessage = i18n.t('email.error.invalidData')
                    } else if (status === 401) {
                      errorMessage = i18n.t('email.error.authFailed')
                    } else if (status >= 500) {
                      errorMessage = i18n.t('email.error.serverError')
                    }
                  }
                  callError(new Error(errorMessage))
                })
            })
            .catch((err) => {
              console.error('sendReadReceipt getMailData failed:', err)
              callError(err)
            })
        } else {
          callError('formAPIs is null')
        }
      }
    })
  }
}

export default SendHelper
